/**
 * Settings Section Registry (search index)
 *
 * Declarative list of the sections inside each settings page, keyed by the
 * module nav href (matching useNavigationItems). Consumed by OmniSearch to
 * offer deep links like "Deals · Pipeline Stages".
 *
 * This is intentionally DATA ONLY so it can live in the main bundle without
 * pulling lazy-loaded page chunks in with it.
 * Section ids must match the SettingsNavItem ids the page passes to
 * SettingsLayout, and pages must resolve their active section from the URL
 * (via useSettingsSection, or their own ?section= handling) for the deep
 * link to land.
 *
 * When adding a section to a settings page, add it here too, and check
 * whether the settings hub needs a matching update in settings-directory.ts
 * (new modules need a SETTINGS_DIRECTORY entry there). Longer term, pages
 * can import their nav item definitions from this registry so there is a
 * single source of truth.
 */

export interface SettingsSectionDef {
  /** Section id as used in the page's SettingsLayout + URL param */
  id: string
  label: string
  keywords?: string[]
}

export interface SettingsPageSections {
  /** URL query param carrying the section id (default 'section') */
  param?: string
  sections: SettingsSectionDef[]
}

/** Keyed by module nav href from useNavigationItems */
export const MODULE_SETTINGS_SECTIONS: Record<string, SettingsPageSections> = {
  '/tasks/mine': {
    sections: [
      { id: 'statuses', label: 'Task Statuses', keywords: ['workflow', 'states'] },
      { id: 'recycle-bin', label: 'Recycle Bin', keywords: ['deleted', 'trash', 'restore'] },
    ],
  },
  '/tickets': {
    sections: [
      { id: 'ticket-types', label: 'Types & Stages', keywords: ['workflow', 'stages'] },
      { id: 'sources', label: 'Sources' },
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'email-intake', label: 'Inboxes', keywords: ['email', 'intake'] },
      { id: 'domains', label: 'Domains', keywords: ['email', 'dns'] },
      { id: 'widget', label: 'Customer Widget', keywords: ['embed', 'chat'] },
      { id: 'alerts', label: 'Alerts', keywords: ['sla', 'escalation'] },
      { id: 'macros', label: 'Macros', keywords: ['canned responses', 'templates'] },
      { id: 'suppressions', label: 'Suppressions', keywords: ['blocklist', 'spam'] },
      { id: 'defaults', label: 'Defaults' },
      { id: 'recycle-bin', label: 'Recycle Bin', keywords: ['deleted', 'trash'] },
    ],
  },
  '/projects': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'statuses', label: 'Statuses', keywords: ['workflow'] },
      { id: 'task-types', label: 'Task Types' },
      { id: 'cycles', label: 'Cycle Templates', keywords: ['sprints', 'iterations'] },
      { id: 'roles-permissions', label: 'Roles & Permissions', keywords: ['access'] },
      { id: 'defaults', label: 'Defaults' },
      { id: 'personal', label: 'My Preferences' },
      { id: 'notifications', label: 'Notifications' },
    ],
  },
  '/deals': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'pipeline', label: 'Pipeline Stages', keywords: ['funnel', 'stages'] },
      { id: 'roles-permissions', label: 'Roles & Permissions', keywords: ['access'] },
      { id: 'scoring', label: 'Scoring', keywords: ['lead scoring'] },
      { id: 'defaults', label: 'Defaults' },
      { id: 'personal', label: 'My Preferences' },
    ],
  },
  '/contacts': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'relationship-types', label: 'Lifecycle', keywords: ['relationship types', 'stages'] },
      { id: 'scoring', label: 'Scoring', keywords: ['lead scoring'] },
      { id: 'categories', label: 'Categories' },
      { id: 'defaults', label: 'Defaults' },
    ],
  },
  '/entities': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'lifecycle', label: 'Lifecycle', keywords: ['stages', 'health'] },
      { id: 'roles-permissions', label: 'Roles & Permissions', keywords: ['access'] },
      { id: 'scoring', label: 'Scoring' },
      { id: 'defaults', label: 'Defaults' },
      { id: 'personal', label: 'My Preferences' },
      { id: 'portal-branding', label: 'Branding', keywords: ['client portal'] },
      { id: 'portal-permission-templates', label: 'Permission Templates', keywords: ['client portal', 'access'] },
    ],
  },
  '/contracts': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'types', label: 'Contract Types' },
      { id: 'legal-statuses', label: 'Legal Statuses' },
      { id: 'reminders', label: 'Reminders', keywords: ['renewal', 'expiry'] },
      { id: 'defaults', label: 'Defaults' },
      { id: 'personal', label: 'My Preferences' },
    ],
  },
  '/invoices': {
    sections: [
      { id: 'defaults', label: 'Invoice Defaults', keywords: ['numbering', 'terms'] },
      { id: 'content', label: 'Content', keywords: ['branding', 'logo'] },
      { id: 'send', label: 'Send & Reminders', keywords: ['email', 'overdue'] },
      { id: 'payments', label: 'Payments', keywords: ['stripe', 'payment methods'] },
    ],
  },
  '/team': {
    sections: [
      { id: 'team-defaults', label: 'Team Defaults' },
      { id: 'default-rates', label: 'Default Rates', keywords: ['billing', 'cost', 'hourly'] },
      { id: 'billing-configuration', label: 'Billing Configuration' },
      { id: 'allocation', label: 'Allocation Policies', keywords: ['capacity'] },
      { id: 'holidays', label: 'Holidays', keywords: ['pto', 'vacation', 'time off'] },
      { id: 'compensation', label: 'Compensation', keywords: ['salary', 'pay'] },
      { id: 'utilization-definitions', label: 'Utilization Definitions' },
      { id: 'skills', label: 'Skills Settings' },
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'roles-permissions', label: 'Roles & Permissions', keywords: ['access'] },
    ],
  },
  '/financials': {
    sections: [
      { id: 'budget-categories', label: 'Budget Categories', keywords: ['budgets'] },
      { id: 'budget-templates', label: 'Budget Templates', keywords: ['budgets'] },
      { id: 'overhead', label: 'Overhead', keywords: ['overhead costs', 'allocation'] },
    ],
  },
  '/timesheets': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'time-format', label: 'Time Format', keywords: ['duration', 'decimal'] },
      { id: 'pay-period', label: 'Pay Period', keywords: ['payroll'] },
      { id: 'expected-hours', label: 'Expected Hours', keywords: ['capacity'] },
      { id: 'entry-rules', label: 'Entry Rules', keywords: ['rounding', 'limits'] },
      { id: 'bill-to', label: 'Bill To' },
      { id: 'approvals', label: 'Approval Workflow', keywords: ['workflow', 'submit'] },
      { id: 'contractor-invoices', label: 'Contractor Invoices' },
      { id: 'notifications', label: 'Notifications' },
      { id: 'maintenance', label: 'Maintenance', keywords: ['rebuild', 'utilization'] },
    ],
  },
  '/products': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'category-types', label: 'Category Types' },
      { id: 'movement-reasons', label: 'Movement Reasons', keywords: ['stock'] },
      { id: 'roles-permissions', label: 'Roles & Permissions', keywords: ['access'] },
    ],
  },
  '/events': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'event-types', label: 'Event Types' },
      { id: 'session-types', label: 'Session Types' },
      { id: 'agenda-types', label: 'Agenda Types' },
      { id: 'speaker-roles', label: 'Speaker Roles' },
      { id: 'host-roles', label: 'Host Roles' },
      { id: 'roles-permissions', label: 'Roles & Permissions', keywords: ['access'] },
      { id: 'reminders', label: 'Reminders' },
      { id: 'defaults', label: 'Defaults' },
    ],
  },
  '/suppliers': {
    sections: [
      { id: 'custom-fields', label: 'Custom Fields', keywords: ['fields'] },
      { id: 'lifecycle', label: 'Lifecycle Stages' },
      { id: 'payment-terms', label: 'Payment Terms', keywords: ['net terms', 'due date'] },
      { id: 'roles-permissions', label: 'Roles & Permissions', keywords: ['access'] },
    ],
  },
  '/expenses': {
    sections: [
      { id: 'defaults', label: 'Defaults' },
      { id: 'categories', label: 'Categories' },
      { id: 'statuses', label: 'Statuses' },
      { id: 'payment-methods', label: 'Payment Methods', keywords: ['cards', 'reimbursement'] },
    ],
  },
}

/** Sections of the admin Organization Settings page (?section=) */
export const ADMIN_SETTINGS_SECTIONS: (SettingsSectionDef & {
  businessOnly?: boolean
  personalOnly?: boolean
})[] = [
  { id: 'general', label: 'General', keywords: ['identity', 'timezone', 'regional', 'locale', 'logo'] },
  { id: 'workspace-defaults', label: 'Workspace Defaults', keywords: ['task defaults', 'project defaults'] },
  { id: 'finance', label: 'Finance', keywords: ['currency', 'tax'], businessOnly: true },
  { id: 'account', label: 'Account', keywords: ['account type', 'convert'], personalOnly: true },
]

/** Hash tabs of the My Profile (user-settings) page */
export const USER_SETTINGS_TABS: SettingsSectionDef[] = [
  { id: 'profile', label: 'Profile', keywords: ['avatar', 'name', 'photo', 'title'] },
  { id: 'skills', label: 'Skills' },
  { id: 'notifications', label: 'Notifications', keywords: ['alerts', 'email', 'push'] },
  { id: 'integrations', label: 'Integrations', keywords: ['slack', 'github', 'outlook', 'gmail', 'connect', 'mcp', 'ai assistants', 'agents', 'api keys', 'browser extension', 'scrambl capture', 'google calendar', 'zoom'] },
]
